/** How the visitor arrived at a step: moving on, going back to one, or the
 * page being loaded again. Funnels count only `forward` views. */
export type StepEntry = "forward" | "back" | "reload";

export const BACK_ENTRY_STATE = { entry: "back" } as const;

type NavigationType = "PUSH" | "POP" | "REPLACE";

/** `documentLoad` is the first view after the page itself loaded, where
 * `documentNavigationType` is the browser's own account of that load: a
 * reload or a history traversal reopens the flow, anything else is the
 * visitor arriving. After that, a history traversal (the browser's Back)
 * and the flow's own Back, which marks its navigation, are `back`. */
export function stepEntryOf({
  documentLoad,
  documentNavigationType,
  navigationType,
  state,
}: {
  documentLoad: boolean;
  documentNavigationType: string | undefined;
  navigationType: NavigationType;
  state: unknown;
}): StepEntry {
  if (documentLoad) {
    return documentNavigationType === "reload" ||
      documentNavigationType === "back_forward"
      ? "reload"
      : "forward";
  }
  if (navigationType === "POP") return "back";
  if (
    typeof state === "object" &&
    state !== null &&
    (state as Record<string, unknown>).entry === BACK_ENTRY_STATE.entry
  ) {
    return "back";
  }
  return "forward";
}

/** The browser's navigation type for the current document, or undefined
 * where it cannot say. */
export function documentNavigationType(): string | undefined {
  try {
    const [entry] = performance.getEntriesByType("navigation");
    return (entry as PerformanceNavigationTiming | undefined)?.type;
  } catch {
    return undefined;
  }
}
